import { useCallback, useState } from 'react';
import useFetch, { ServiceParams } from '../hooks/useFetch';
import EndPoints from '../config/EndPoint';
import { ShiftGroup } from '../models/ShiftGroupModel';

const qs = (filters?: Record<string, any>) => {
  if (!filters) return '';
  return Object.entries(filters)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
};

const useShiftGroupsService = () => {
  const { doGet, doPost, doPut, doDelete } = useFetch();
  const [fetching, setFetching] = useState<Record<string, boolean>>({});

  const setFlag = useCallback((key: string, val: boolean) => {
    setFetching(prev => ({ ...prev, [key]: val }));
  }, []);

  const list = useCallback(
    (filters?: Record<string, any>, events?: any, signal?: AbortSignal) =>
      doGet({
        url: `${EndPoints.SHIFT_GROUPS.LIST}${qs(filters) ? `?${qs(filters)}` : ""}`,
        setFetching: v => setFlag('list', v),
        signal,
        ...events
      } as ServiceParams),
    [doGet, setFlag]
  );

  const store = useCallback(
    (payload: ShiftGroup, events?: any) =>
      doPost({ url: EndPoints.SHIFT_GROUPS.STORE, body: payload, setFetching: v => setFlag('store', v), ...events } as ServiceParams),
    [doPost, setFlag]
  );

  const update = useCallback(
    (id: number, payload: ShiftGroup, events?: any) =>
      doPut({ url: EndPoints.SHIFT_GROUPS.UPDATE.replace(':id', String(id)), body: payload, setFetching: v => setFlag('update', v), ...events } as ServiceParams),
    [doPut, setFlag]
  );

  const remove = useCallback(
    (id: number, events?: any) =>
      doDelete({ url: EndPoints.SHIFT_GROUPS.DELETE.replace(':id', String(id)), setFetching: v => setFlag('delete', v), ...events } as ServiceParams),
    [doDelete, setFlag]
  );

  return { fetching, list, store, update, remove };
};

export default useShiftGroupsService;
